/**
 * Bounded, session-scoped continuation for the MCP OAuth consent detour.
 * (Integration plan docs/RITHMO_SHARED_INTEGRATION_PLAN.md — Stage 3, Correction 1.)
 *
 * The problem: when a signed-out person opens /mcp-authorize?external_auth_id=… from their AI assistant, the original
 * authorization must survive a login / company-or-global SSO / invitation / verify-email detour and resume afterwards.
 *
 * The carrier: a SINGLE bounded record in `sessionStorage` holding ONLY the opaque authorization handle + the routing
 * hints + an explicit expiry — never the full OAuth query (redirect_uri / PKCE / state / scope / nonce are dropped;
 * WorkOS holds those bound to external_auth_id, and the SSO leg carries external_auth_id in the backend return_to).
 *
 * Why sessionStorage, and its real limits (MDN): it is session-scoped, but it DOES survive page reloads/restores and
 * CAN be copied into an opener-created tab (a `window.open` without `noopener` seeds the child tab's sessionStorage from
 * the opener). It is therefore NOT "never stored" — so the handle is kept minimal, short-lived (TTL) and cleared the
 * moment it is resumed, cancelled, expires or the user logs out. The handle is also kept out of analytics
 * (mcpAuthorizeModel.analyticsPath drops the /mcp-authorize query) and is never logged.
 *
 * Fault-safe: every read/write is guarded. When web storage is unavailable (private mode / disabled / quota) the journey
 * falls back to the URL + router-state carriage (the SSO leg's return_to, and `from` for password login) and still
 * completes — it never hard-fails and never loops.
 */

export const OAUTH_CONTINUATION_KEY = 'rithmo_oauth_continuation'
const DEFAULT_TTL_MS = 10 * 60 * 1000 // a consent detour is short; a stale handle must not linger

// The ONLY fields carried: the opaque handle + the two routing hints. NOT the full OAuth query.
const BOUNDED_KEYS = ['external_auth_id', 'connection', 'sso_org', 'assistant']

function sessionStore() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null
  } catch {
    return null // accessing sessionStorage can itself throw (sandboxed iframe / disabled)
  }
}

/** The bounded context query string from a location search — only the handle + routing hints. '' when there is no handle. */
export function boundedContext(search) {
  const p = new URLSearchParams(search || '')
  if (!(p.get('external_auth_id') || '').trim()) return '' // the handle is the anchor; nothing to carry without it
  const out = new URLSearchParams()
  for (const k of BOUNDED_KEYS) {
    const v = (p.get(k) || '').trim()
    if (v) out.set(k, v)
  }
  return out.toString()
}

/**
 * Persist the bounded continuation (+ expiry). Fault-safe. No-op when the search carries no handle. Returns TRUE only when
 * the continuation was actually written to storage; FALSE when there is no handle OR storage is unavailable — the caller
 * then carries the bounded context on the detour URL instead (so the journey still resumes).
 */
export function saveContinuation(search, { ttlMs = DEFAULT_TTL_MS, nowMs = Date.now() } = {}) {
  const ctx = boundedContext(search)
  if (!ctx) return false
  const s = sessionStore()
  if (!s) return false // storage unavailable → URL/router-state carriage still works
  try {
    s.setItem(OAUTH_CONTINUATION_KEY, JSON.stringify({ ctx, exp: nowMs + ttlMs }))
    return true
  } catch {
    return false // quota / disabled → fall back to URL carriage
  }
}

/**
 * Whether sessionStorage can actually hold the continuation this session (private mode / disabled / quota → false).
 * When false, the journey carries the bounded context on the detour URLs instead (kept out of analytics by analyticsPath,
 * and off the URL entirely in the normal case where storage works).
 */
export function isSessionStorageAvailable() {
  const s = sessionStore()
  if (!s) return false
  const probe = '__rithmo_ss_probe__'
  try {
    s.setItem(probe, '1')
    const ok = s.getItem(probe) === '1'
    s.removeItem(probe)
    return ok
  } catch {
    return false
  }
}

/** The bounded continuation from storage, OR — when storage is unavailable — from the current URL (the fallback carriage). */
export function loadContinuationOrUrl(search) {
  return loadContinuation() || boundedContext(search)
}

/**
 * Append the bounded context to a path as the URL FALLBACK carriage (used only when sessionStorage is unavailable, so the
 * handle stays off URLs/history in the normal case). Re-bounds the context, and does not duplicate keys already present.
 */
export function appendContinuation(path, ctx) {
  const bounded = boundedContext(`?${ctx || ''}`)
  if (!bounded) return path
  const [base, existing = ''] = path.split('?')
  const out = new URLSearchParams(existing)
  for (const [k, v] of new URLSearchParams(bounded)) if (!out.has(k)) out.set(k, v)
  const qs = out.toString()
  return qs ? `${base}?${qs}` : base
}

/** The stored bounded context string if present AND unexpired; '' otherwise. Expired/malformed entries are cleared. */
export function loadContinuation({ nowMs = Date.now() } = {}) {
  const s = sessionStore()
  if (!s) return ''
  let raw = null
  try {
    raw = s.getItem(OAUTH_CONTINUATION_KEY)
  } catch {
    return ''
  }
  if (!raw) return ''
  let parsed = null
  try {
    parsed = JSON.parse(raw)
  } catch {
    clearContinuation()
    return ''
  }
  if (!parsed || typeof parsed.ctx !== 'string' || !parsed.ctx || typeof parsed.exp !== 'number' || nowMs > parsed.exp) {
    clearContinuation()
    return ''
  }
  // Defense in depth: never hand back more than the bounded fields even if an older/forged record carried extra.
  return boundedContext(`?${parsed.ctx}`)
}

/** Remove the continuation unconditionally. Fault-safe. */
export function clearContinuation() {
  const s = sessionStore()
  if (!s) return
  try {
    s.removeItem(OAUTH_CONTINUATION_KEY)
  } catch {
    /* ignore */
  }
}

/**
 * Clear ONLY when the stored handle matches `handle` (R3-5): once the consent page is shown authenticated for THIS
 * external_auth_id the saved copy has done its job, but an UNRELATED pending authorization must never be discarded.
 */
export function clearMatching(handle) {
  const ctx = loadContinuation()
  if (!ctx || !handle) return
  if (new URLSearchParams(ctx).get('external_auth_id') === handle) clearContinuation()
}

/** The external_auth_id currently held in the continuation (or ''), for match/debug checks. */
export function continuationHandle() {
  const ctx = loadContinuation()
  return ctx ? (new URLSearchParams(ctx).get('external_auth_id') || '') : ''
}

/**
 * True iff the stored continuation is present AND is for THIS request's handle. The persistence check must be
 * request-SPECIFIC: if an OLDER authorization A is still stored and the CURRENT request B's write failed (quota), a bare
 * `loadContinuation()` returns A — the page would then suppress B's URL fallback and later resume the WRONG authorization.
 * Callers use this (not a bare load) so B is carried on the URL when storage does not hold B specifically.
 */
export function continuationMatches(handle) {
  const h = (handle || '').trim()
  if (!h) return false
  const ctx = loadContinuation()
  return !!ctx && new URLSearchParams(ctx).get('external_auth_id') === h
}

/** The handle carried in a bounded-context string (or ''). */
export function handleOf(ctx) {
  return ctx ? (new URLSearchParams(ctx).get('external_auth_id') || '') : ''
}
